import React, { useState } from "react";
import { FileSpreadsheet, Loader2, Printer } from "lucide-react";
import {
  useGetChapterRegisterQuery,
  useLazyGetChapterRegisterQuery,
  type ChapterRegisterMember,
} from "../api/chapterApi";
import { useGetActiveUserCategoriesQuery } from "../../settings/api/settingsApi";
import DataTable from "../../membership/components/DataTable";
import TableSkeleton from "../../membership/components/TableSkeleton";
import useDebounce from "../../membership/components/useDebounce";
import {
  exportChapterRegisterPdf,
  exportChapterRegisterSpreadsheet,
  fullName,
  genderLabel,
  standing,
  type ChapterRegisterExportMeta,
} from "../utils/exportChapterRegister";

/**
 * The chapter's membership register, as its sitting chairman sees it.
 *
 * Same register the secretariat reads at /member-list, minus the two things
 * that are not a chapter's business: the naira figure of what a member owes,
 * and the member record behind the row. Dues appear as up to date or not and
 * nothing finer — the server sends a flag, not a balance — and there is no
 * "View" action, because there is nothing behind these rows to open.
 *
 * There is no chapter filter either. Which chapter this is comes off the
 * chairman's leadership record on the server; the screen has nothing to choose.
 *
 * It downloads, as a spreadsheet or a printed sheet, in the columns shown here
 * and no others — a chairman works this list off the screen for meeting
 * notices, quorums and endorsements. The download is the whole filtered
 * register rather than the page on view: the server pages at 100, so it is
 * walked page by page before anything is written.
 */
export default function ChapterRegisterPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("surname");
  const [dir, setDir] = useState("asc");
  const [grade, setGrade] = useState("");
  const [gender, setGender] = useState("");
  const [status, setStatus] = useState("");
  const [verified, setVerified] = useState("");
  const [dues, setDues] = useState("");
  const [chapterId, setChapterId] = useState("");

  const debouncedSearch = useDebounce(search);

  /**
   * What the screen is asking for, in one place.
   *
   * The table and the download read the SAME object — only the page and the
   * page size differ — so a download can never come back as a different list
   * from the one it was taken from.
   */
  const query = {
    search: debouncedSearch,
    sort_by: sort,
    sort_dir: dir,
    grade,
    gender,
    status,
    verified,
    dues,
    chapter_id: chapterId,
  };

  const { data, isLoading, isFetching, error } = useGetChapterRegisterQuery({
    ...query,
    page,
    per_page: perPage,
  });

  const { data: categories } = useGetActiveUserCategoriesQuery();

  // DataTable owns a selection column; nothing here acts on a selection, so it
  // is held and ignored.
  const [selected, setSelected] = useState([]);

  const notALeader = (error as any)?.status === 403;
  const actingFor = data?.acting_for ?? [];
  const pagination = data?.members;

  /* ---------- DOWNLOAD ---------- */
  /*
   * fullName, genderLabel and standing live in ../utils/exportChapterRegister
   * with the download that shares them: the printed list has to read the same
   * as the screen, and two copies of "which spelling of M means Male" would
   * eventually disagree.
   */

  const [fetchRegisterPage] = useLazyGetChapterRegisterQuery();
  const [downloading, setDownloading] = useState<"csv" | "pdf" | null>(null);

  /** The chapters whose rows can appear here — his own, plus any he covers. */
  const myChapters = [
    ...(data?.chapter ? [{ id: String(data.chapter.id), name: data.chapter.name }] : []),
    ...actingFor.map((c: any) => ({ id: String(c.id), name: c.name })),
  ];

  /** Sortable columns in words, for the line that explains the order on paper. */
  const SORT_LABELS: Record<string, string> = {
    surname: "surname",
    firstname: "first name",
    membership_no: "membership number",
    email: "email",
    grade: "grade",
    reg_date: "date registered",
    created_at: "date added",
    status: "status",
    verification: "verification",
    dues: "dues",
  };

  /**
   * The filters that were on, written out for the header of the download.
   *
   * A register of 40 names out of a chapter of 300 is a misleading document
   * unless it says on its face that it was filtered, and by what.
   */
  const filterNotes = (): string[] => {
    const notes: string[] = [];
    const named = (id: string, list: any[]) =>
      list?.find((o: any) => String(o.id) === String(id))?.name;

    if (debouncedSearch.trim()) notes.push(`Search: “${debouncedSearch.trim()}”`);
    if (grade) notes.push(`Grade: ${named(grade, (categories as any) ?? []) ?? grade}`);
    if (gender) notes.push(`Gender: ${gender === "male" ? "Male" : "Female"}`);
    if (status) notes.push(`Status: ${status.charAt(0).toUpperCase() + status.slice(1)}`);
    if (verified) notes.push(`Verification: ${verified === "verified" ? "Verified" : "Unverified"}`);
    if (dues) notes.push(`Dues: ${dues === "owing" ? "Not up to date" : "Up to date"}`);
    if (chapterId) notes.push(`Chapter: ${named(chapterId, myChapters) ?? chapterId}`);

    return notes.length ? notes : ["The whole register — no filters applied"];
  };

  /**
   * Every member the filters match, not the page on screen.
   *
   * The server caps per_page at 100 on purpose, so the download walks the pages
   * and stops as soon as it holds the total the paginator reported. The loop
   * ceiling is a guard against a server that never stops paging, not a limit a
   * chapter reaches — 100 pages is 10,000 members.
   */
  const collectEveryMember = async (): Promise<ChapterRegisterMember[]> => {
    const rows: ChapterRegisterMember[] = [];

    for (let p = 1; p <= 100; p += 1) {
      const res = await fetchRegisterPage({ ...query, page: p, per_page: 100 }).unwrap();
      const chunk = res.members?.data ?? [];
      rows.push(...chunk);

      if (!chunk.length || rows.length >= (res.members?.total ?? rows.length)) break;
    }

    return rows;
  };

  const download = async (as: "csv" | "pdf") => {
    if (downloading) return;

    /*
     * The print window is opened HERE, in the click itself, and handed to the
     * export. Opened after the fetch instead, it is a pop-up with no gesture
     * behind it and every browser blocks it.
     */
    let printWindow: Window | null = null;
    if (as === "pdf") {
      printWindow = window.open("", "_blank", "width=1100,height=800");
      if (!printWindow) {
        alert("Allow pop-ups for this site to print the register.");
        return;
      }
      printWindow.document.write(
        '<!DOCTYPE html><title>Preparing…</title><body style="font:14px \'Segoe UI\',Arial;padding:24px;color:#555">Collecting the register…</body>',
      );
    }

    setDownloading(as);

    try {
      const rows = await collectEveryMember();

      const meta: ChapterRegisterExportMeta = {
        chapter: data?.chapter?.name || "NIQS",
        // Only where his rows come from more than one register; the same test
        // the table uses to grow the column.
        withChapterColumn: !!actingFor.length,
        filters: filterNotes(),
        order: `Sorted by ${SORT_LABELS[sort] ?? sort}, ${dir === "desc" ? "descending" : "ascending"}`,
      };

      if (as === "csv") exportChapterRegisterSpreadsheet(rows, meta);
      else exportChapterRegisterPdf(rows, meta, printWindow);
    } catch (e: any) {
      printWindow?.close();
      alert(
        e?.data?.message ||
          "The register could not be downloaded. Please check your connection and try again.",
      );
    } finally {
      setDownloading(null);
    }
  };

  const columns = [
    { key: "surname", header: "Full Name", accessor: (r: any) => fullName(r) || "—" },
    {
      key: "membership_no",
      header: "Membership No",
      // The number issued on upgrade takes precedence where a member has one —
      // it is the number they are called by once they hold it.
      accessor: (r: any) => r.member_member_no || r.membership_no || "—",
    },
    { key: "grade", header: "Grade", accessor: (r: any) => r.grade?.name ?? "—" },
    { key: "email", header: "Email", accessor: (r: any) => r.email || "—" },
    {
      key: "gender",
      header: "Gender",
      sortable: false,
      accessor: (r: any) => genderLabel(r.gender),
    },
    {
      key: "status",
      header: "Status",
      accessor: (r: any) => {
        const { label, tone } = standing(r);
        return (
          <span className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${tone}`}>
            {label}
          </span>
        );
      },
    },
    {
      // The same badge the secretariat's register carries, so the two screens
      // say the same thing about a member.
      key: "verification",
      header: "Verification",
      sortable: false,
      accessor: (r: any) => (
        <span
          className={
            r.account_verified_at
              ? "inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200"
              : "inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
          }
        >
          {r.account_verified_at ? "Verified" : "Unverified"}
        </span>
      ),
    },
    {
      // Whether, not how much. The server sends a flag and no balance, so there
      // is no figure here to print even if this column wanted one.
      key: "dues",
      header: "Dues",
      accessor: (r: any) => (
        <span
          className={
            r.owes_dues
              ? "inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200"
              : "inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200"
          }
        >
          {r.owes_dues ? "Not up to date" : "Up to date"}
        </span>
      ),
    },
    {
      key: "reg_date",
      header: "Registered",
      accessor: (r: any) => (r.reg_date ? String(r.reg_date).slice(0, 10) : "—"),
    },
    { key: "state", header: "State", sortable: false, accessor: (r: any) => r.state?.name ?? "—" },
    { key: "lga", header: "LGA", sortable: false, accessor: (r: any) => r.lga?.name ?? "—" },
  ];

  // Only worth a column when he is looking at more than one chapter's members;
  // otherwise every row would repeat the name in the heading.
  if (actingFor.length) {
    columns.splice(1, 0, {
      key: "chapter",
      header: "Chapter",
      sortable: false,
      accessor: (r: any) => r.chapter?.name ?? "—",
    } as any);
  }

  /** One member as a card, for screens the table does not fit. */
  const mobileCard = (r: any) => (
    <div className="space-y-2">
      <div className="font-bold text-gray-900 leading-tight break-words">
        {fullName(r) || "—"}
      </div>
      <div className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
        {r.member_member_no || r.membership_no || "No member no."}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
          {r.grade?.name ?? "No grade"}
        </span>
        <span
          className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full border ${standing(r).tone}`}
        >
          {standing(r).label}
        </span>
        <span
          className={
            r.account_verified_at
              ? "inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200"
              : "inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
          }
        >
          {r.account_verified_at ? "Verified" : "Unverified"}
        </span>
        <span
          className={
            r.owes_dues
              ? "inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200"
              : "inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200"
          }
        >
          {r.owes_dues ? "Not up to date" : "Up to date"}
        </span>
      </div>
      {/* Long addresses wrap rather than stretching the card sideways. */}
      {r.email && (
        <div className="text-xs text-gray-500 font-medium break-words">{r.email}</div>
      )}
      <div className="text-[11px] text-gray-400 font-medium break-words">
        {[
          genderLabel(r.gender) !== "—" ? genderLabel(r.gender) : null,
          actingFor.length ? r.chapter?.name : null,
          r.state?.name,
          r.lga?.name,
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
    </div>
  );

  if (notALeader) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <div className="px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-sm font-semibold text-amber-800">
          {(error as any)?.data?.message ||
            "You are not the sitting chairman of any active chapter."}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white rounded-lg shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
            {data?.chapter?.name ? `${data.chapter.name} Chapter Register` : "Chapter Register"}
          </h2>
          <p className="text-xs text-gray-500 font-medium mt-1">
            The members of your chapter. Dues read as up to date or not — the
            amount, and the invoices behind it, stay with the secretariat.
          </p>
          {!!actingFor.length && (
            <p className="text-[11px] text-gray-400 font-medium mt-1">
              Also acting for: {actingFor.map((c: any) => c.name).join(", ")}
            </p>
          )}
        </div>

        {/* Both downloads carry the register as the filters leave it — every
            member that matches, not the page on screen, and the same columns in
            the same order the table is showing them in. */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => download("csv")}
            disabled={isLoading || !!downloading}
            title="Download this register as a spreadsheet"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50"
          >
            {downloading === "csv" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            {downloading === "csv" ? "Collecting…" : "Excel"}
          </button>
          <button
            onClick={() => download("pdf")}
            disabled={isLoading || !!downloading}
            title="Print this register, or save it as a PDF"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50"
          >
            {downloading === "pdf" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Printer className="w-4 h-4" />
            )}
            {downloading === "pdf" ? "Collecting…" : "PDF"}
          </button>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          <input
            placeholder="Member no / name…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white focus:border-blue-500 outline-none"
          />

          <select
            value={grade}
            onChange={(e) => {
              setGrade(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All Grades</option>
            {categories?.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {/* "male"/"female", not "M"/"Male" — the column holds every spelling
              of both, so the server matches on the initial and the value sent
              here only has to say which of the two is meant. */}
          <select
            value={gender}
            onChange={(e) => {
              setGender(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All Genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deceased">Deceased</option>
          </select>

          <select
            value={verified}
            onChange={(e) => {
              setVerified(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All Verifications</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>

          <select
            value={dues}
            onChange={(e) => {
              setDues(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All Dues</option>
            <option value="up_to_date">Financially up to date</option>
            <option value="owing">Not up to date</option>
          </select>

          {/* Offered only when he covers a second chapter — otherwise there is
              exactly one register and nothing to pick between. */}
          {!!actingFor.length && (
            <select
              value={chapterId}
              onChange={(e) => {
                setChapterId(e.target.value);
                setPage(1);
              }}
              className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
            >
              <option value="">All my chapters</option>
              {data?.chapter && (
                <option value={String(data.chapter.id)}>{data.chapter.name}</option>
              )}
              {actingFor.map((c: any) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          )}

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value={25}>25 Rows</option>
            <option value={50}>50 Rows</option>
            <option value={100}>100 Rows</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : (
        <div className={isFetching ? "opacity-60 transition-opacity" : ""}>
          <DataTable
            columns={columns}
            data={pagination?.data || []}
            pagination={pagination}
            onPageChange={setPage}
            sort={sort}
            setSort={setSort}
            dir={dir}
            setDir={setDir}
            selected={selected}
            setSelected={setSelected}
            mobileCard={mobileCard}
          />
        </div>
      )}
    </div>
  );
}
